import { parseTimestamp } from '@/util/zod/validators'
import { z } from 'zod'

// ArtifactVersionStatusType Enum
export enum ArtifactVersionStatusType {
  GENERATING = 'generating',
  COMPLETE = 'complete',
  ERROR = 'error',
  CANCELLED = 'cancelled',
}

/**
 * FileExtensionType Enum
 * This is for artifacts specifically, and a separate concern from file uploads.
 */
export enum ArtifactVersionFileExtensionType {
  MD = 'md',
  DOCX = 'docx',
  PDF = 'pdf',
  XLSX = 'xlsx',
}

// Based on the BE ArtifactVersionRead model
export const ArtifactVersionReadSchema = z.object({
  id: z.string().uuid(),
  artifact_id: z.string().uuid(),
  status: z.nativeEnum(ArtifactVersionStatusType),
  file_extension: z.nativeEnum(ArtifactVersionFileExtensionType),
  name: z.string().nullable(),
  summary: z.string().nullable(),
  created_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number()),
  // Bumped on every content write (auto-save / inline edit); used to detect a draft edited after a
  // template was applied (so the rendered output is stale).
  updated_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number()),
})

export type ArtifactVersionRead = z.infer<typeof ArtifactVersionReadSchema>

export const ArtifactVersionViewableUrlResponseSchema = z.object({
  artifact_version_id: z.string().uuid(),
  viewable_url: z.string().min(1),
  expires_at: z.string(),
  content_type: z.string().min(1),
})

export type ArtifactVersionViewableUrlResponse = z.infer<typeof ArtifactVersionViewableUrlResponseSchema>

// Based on the BE WS2AgentConversationArtifactVersion model
export const ArtifactVersionUpdatedSchema = z.object({
  turn_id: z.string().uuid(),
  status_event_id: z.string().uuid(),
  response_event_id: z.string().uuid(),
  artifact_version: ArtifactVersionReadSchema,
})

export type ArtifactVersionUpdated = z.infer<typeof ArtifactVersionUpdatedSchema>

// Update Artifact Version API schemas
export const UpdateArtifactVersionRequestSchema = z.object({
  artifactVersionId: z.string(),
  content: z.string(),
  parentVersionId: z.string().optional(),
})

export type UpdateArtifactVersionRequest = z.infer<typeof UpdateArtifactVersionRequestSchema>
